import {HeaderBackButtonProps} from '@react-navigation/elements';
import {useIsFocused} from '@react-navigation/native';
import React from 'react';
import {Dimensions, View} from 'react-native';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {BackButton} from '../../components/BackButton';
import {BottomSheetComponent} from '../../components/BottomSheet';
import {BottomSheetButtonTitle} from '../../components/BottomSheetButton';
import {BottomSheetDetail} from '../../components/BottomSheetDetail';
import {Button} from '../../components/Button';
import {Carousel} from '../../components/Carousel';
import {GlowingHalo} from '../../components/GlowingHalo';
import {HeatProfileScreenBaseBackground} from '../../components/HeatProfileScreenBaseBackground';
import {MultiGradientBackground} from '../../components/MultiGradientBackground';
import {ProfileDisplay} from '../../components/ProfileDisplay';
import {StyledButton} from '../../components/StyledButton';
import {Constants} from '../../constants';
import {Alerts, Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {useSpinner} from '../../contexts/spinner';
import {useRealtimeUnitLighting} from '../../contexts/unit-lighting/useRealtimeUnitLight';
import {DeleteIcon, MoreIcon, SettingsIcon, ShareIcon} from '../../icons';
import {HeatCycleArrayIndex} from '../../lib/ble2/v2/pikaparam';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useAppHeaderHeight} from '../../lib/hooks/useAppHeaderHeight';
import {useChamberType} from '../../lib/hooks/useChamberType';
import {useDeleteAccountHeatProfile} from '../../lib/hooks/useDeleteAccountHeatProfile';
import {useDisconnectGuard} from '../../lib/hooks/useDisconnectGuard';
import {useShareHeatProfile} from '../../lib/hooks/useShareHeatProfile';
import {useTheme} from '../../lib/hooks/useTheme';
import {useUpdateDabbingReduxValues} from '../../lib/hooks/useUpdateDabbingReduxValues';
import {useWatchDevice} from '../../lib/hooks/useWatchDevice';
import {
  connectedPeakSelector,
  currentDeviceHeatProfilesSelector,
  currentDeviceSelectedHeatProfileSelector,
  currentDeviceSelector,
} from '../../lib/redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {accountHeatProfilesSelector} from '../../lib/redux/slices/profilesSlice';
import {userSelector} from '../../lib/redux/slices/userSlice';
import {
  HeatProfile,
  MoodLight,
  Vapor,
  getHeatProfileColors,
  getHeatProfileLight,
  getHeatProfileMoodLight,
} from '../../lib/types';
import {meetsMinimumFirmware} from '../../lib/utils';
import styled from '../../lib/utils/styled';
import {toHeatCycle} from '../../lib/utils/toHeatCycle';
import type {HomeEmulatedDrawerStackScreenProps} from '../../navigators/HomeDrawerNavigator';
import {toHeatProfileEdit} from '../../navigators/util';
import {analytics} from '../../services/analytics';
import {
  convertChamberType,
  convertProduct,
  convertVaporMode,
} from '../../services/analytics/converters';
import {centerStyle, colors} from '../../styles';
import {
  blackPeachTheme,
  desertTheme,
  opalTheme,
  whitePeachTheme,
} from '../../themes';
import {useCanDab} from './useCanDab';

export type HeatProfileRenderItemType = HeatProfile & {
  width?: number;
  height: number;
};

const WIDTH = Dimensions.get('window').width;
const INDICATOR_HEIGHT = 92;

export interface Props {
  profileId: string;
}

interface ScreenProps
  extends HomeEmulatedDrawerStackScreenProps<
    typeof Screens.HeatProfileSelect
  > {}

export const HeatProfileSelectScreen = ({route, navigation}: ScreenProps) => {
  useDisconnectGuard();

  const theme = useTheme();

  const user = useSelector(userSelector);
  const peak = useSelector(connectedPeakSelector);
  const device = useSelector(currentDeviceSelector);
  const deviceHeatProfiles = useSelector(currentDeviceHeatProfilesSelector);

  const {verifyCanDab} = useCanDab();

  const {profileId} = route.params ?? {};

  const isDeviceHeatProfile = deviceHeatProfiles.some(
    ({id}) => id === profileId,
  );

  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const getMoodLight = React.useCallback(
    (id: string) => moodLights[id] as MoodLight | undefined,
    [moodLights],
  );

  const profiles = useSelector(
    isDeviceHeatProfile
      ? currentDeviceHeatProfilesSelector
      : accountHeatProfilesSelector,
  );

  const currentSelectedHeatProfile = useSelector(
    currentDeviceSelectedHeatProfileSelector,
  );

  const currentProfile =
    profiles.find(p => p.id === profileId) ?? currentSelectedHeatProfile;

  const isFocused = useIsFocused();

  useWatchDevice('heatCycleSelect', isFocused);

  const enabled = React.useRef(false);
  const tid = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    if (isFocused) {
      // Delay reading currentProfileIndex since it may be out of sync initially and the correct profile is already in route params
      tid.current = setTimeout(() => {
        enabled.current = true;
      }, 1000);
      return;
    }

    clearTimeout(tid.current);
    enabled.current = false;
  }, [isFocused]);

  React.useEffect(() => {
    if (!isDeviceHeatProfile || !currentSelectedHeatProfile || !enabled.current)
      return;

    navigation.setParams({
      profileId: currentSelectedHeatProfile.id,
    });
  }, [isDeviceHeatProfile, currentSelectedHeatProfile]);

  const index = isDeviceHeatProfile ? currentProfile?.order ?? 0 : 0;

  React.useEffect(() => {
    if (!peak || !currentProfile) return;

    if (isDeviceHeatProfile) {
      peak.cycles.writeSelectedHeatCycle(
        currentProfile.order as HeatCycleArrayIndex,
      );
    } else {
      peak.cycles.writeTempHeatCycle(toHeatCycle(currentProfile, getMoodLight));
    }
  }, [peak, currentProfile, isDeviceHeatProfile, getMoodLight]);

  const {isVaporEnabled} = useChamberType();

  const isOpal = opalTheme === theme;
  const isPeachOrDesert =
    theme === whitePeachTheme ||
    theme === blackPeachTheme ||
    theme === desertTheme;

  const moodLightColors = React.useMemo(
    () =>
      currentProfile
        ? getHeatProfileColors(moodLights)(currentProfile)
        : ['#ffffff'],
    [moodLights, currentProfile],
  );

  const HEADER_HEIGHT = useAppHeaderHeight();
  const [dabLoading, setDabLoading] = React.useState(false);

  useRealtimeUnitLighting(
    Screens.HeatProfileSelect,
    currentProfile
      ? getHeatProfileLight(moodLights)(currentProfile)
      : undefined,
  );

  const updateDabbingReduxValues = useUpdateDabbingReduxValues();
  useAdaptiveSafeArea();
  const [backgroundHeight, setBackgroundHeight] = React.useState<
    number | undefined
  >(undefined);

  const [{loading: isDeleting}, deleteHeatProfile] =
    useDeleteAccountHeatProfile();
  const [{loading: isSharing}, share] = useShareHeatProfile();

  const isLoading = isDeleting || isSharing;

  useSpinner({
    isVisible: isLoading && isFocused,
    ...(isLoading && {
      text: isSharing ? Strings.PREPARING_PROFILE_SHARE : Strings.SAVING,
      color: theme.alternateSpinnerColor,
    }),
  });

  React.useEffect(() => {
    navigation.setOptions({
      headerTitle: isDeviceHeatProfile !== false ? 'ACTIVE' : 'ARCHIVE',
      headerTitleStyle: {
        ...theme.navMenuTitleStyle,
        ...(isPeachOrDesert && {color: colors.white}),
      },
      headerLeft: ({disabled, onPress}: HeaderBackButtonProps) => (
        <BackButton
          {...{disabled, onPress}}
          color={isPeachOrDesert ? colors.white : theme.navMenuIconColor}
        />
      ),
      gestureEnabled: false,
    });
  }, [
    navigation,
    isDeviceHeatProfile,
    theme.navMenuTitleStyle,
    isPeachOrDesert,
    theme.navMenuIconColor,
  ]);

  const onMoreDotsPress = React.useCallback(() => {
    if (!currentProfile) return;

    const content = [
      {
        title: BottomSheetButtonTitle.EDIT,
        icon: SettingsIcon,
        onPress: () => {
          navigation.navigate(
            ...toHeatProfileEdit({profileId: currentProfile.id}).value,
          );
        },
      },
      {
        title: BottomSheetButtonTitle.SHARE,
        icon: ShareIcon,
        onPress: () => {
          share(currentProfile.id);
        },
      },
      ...(!isDeviceHeatProfile && user
        ? [
            {
              title: BottomSheetButtonTitle.DELETE,
              icon: DeleteIcon,
              onPress: () =>
                deleteHeatProfile({
                  id: currentProfile.id,
                  onDelete: () => {
                    analytics.trackEvent('heat profile delete', {});

                    navigation.goBack();
                  },
                }),
            },
          ]
        : []),
    ];

    BottomSheetComponent.display(
      <BottomSheetDetail
        header={{
          icon: (
            <MultiGradientBackground
              gradientShadeFunction={
                theme.bottomSheetTheme.gradientShadeFunction
              }
              colors={moodLightColors}
              style={{...centerStyle, width: 40, height: 40}}
              {...(theme.bottomSheetTheme.IconBaseBackground && {
                BaseBackground: theme.bottomSheetTheme.IconBaseBackground,
              })}>
              <GlowingHalo
                colors={moodLightColors}
                theme={theme}
                seconds={currentProfile.duration}
                glowWidthHeight={34}
              />
            </MultiGradientBackground>
          ),
          title: currentProfile.name,
          subtitle: 'HEAT PROFILE',
        }}
        buttonPropArr={content}
      />,
    );
  }, [
    currentProfile,
    deleteHeatProfile,
    isDeviceHeatProfile,
    moodLightColors,
    share,
    theme,
    user,
  ]);

  const iconColor = isPeachOrDesert ? colors.white : theme.navMenuIconColor;

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button variant="transparent" onPress={onMoreDotsPress}>
          <MoreIcon size={24} color={iconColor} />
        </Button>
      ),
    });
  }, [onMoreDotsPress, iconColor]);

  const dabWith = React.useCallback(
    async (profile: HeatProfile) => {
      if (dabLoading) return;

      const moodLight = getHeatProfileMoodLight(moodLights)(profile);

      verifyCanDab(async () => {
        if (isDeviceHeatProfile) {
          await peak?.cycles.writeSelectedHeatCycle(
            profile.order as HeatCycleArrayIndex,
          );
          updateDabbingReduxValues(profile);
          setDabLoading(true);

          peak?.startDabbing(profile.order).then(() =>
            analytics.trackEvent('heat cycle start', {
              profile_id: profile.id,
              profile_name: profile.name,
              product: convertProduct(peak.product),
              ...(device?.chamberType && {
                chamber_type: convertChamberType(device.chamberType),
              }),
              ...(profile.vaporSetting !== undefined && {
                vapor_mode: convertVaporMode(
                  profile.vaporSetting as Vapor | undefined,
                ),
              }),
              ...(moodLight && {
                mood_light_id: moodLight.id,
                mood_light_type: moodLight.category,
              }),
              duration: profile.duration,
              temperature: profile.temperature,
              timestamp: Date.now(),
            }),
          );

          setDabLoading(false);
          navigation.navigate(Screens.Dabbing);
        } else {
          if (
            !meetsMinimumFirmware(
              device?.softwareRevisionString,
              Constants.MINIMUM_FIRMWARE_VERSION.TEMP_PROFILE,
            )
          ) {
            return Alert.alert(
              Alerts.UPDATE_FIRMWARE,
              Alerts.UPDATE_FIRMWARE_BODY,
            );
          }

          await peak?.cycles.writeTempHeatCycle(
            toHeatCycle(profile, () => moodLight),
          );

          await peak?.cycles.writeSelectedHeatCycle(
            Constants.TEMP_HEAT_PROFILE_INDEX,
          );

          updateDabbingReduxValues(profile);
          setDabLoading(true);

          peak?.startDabbing(Constants.TEMP_HEAT_PROFILE_INDEX).then(() =>
            analytics.trackEvent('heat cycle start', {
              profile_id: profile.id,
              profile_name: profile.name,
              product: convertProduct(peak.product),
              ...(device?.chamberType && {
                chamber_type: convertChamberType(device.chamberType),
              }),
              ...(profile.vaporSetting !== undefined && {
                vapor_mode: convertVaporMode(
                  profile.vaporSetting as Vapor | undefined,
                ),
              }),
              ...(moodLight && {
                mood_light_id: moodLight.id,
                mood_light_type: moodLight?.category,
              }),
              duration: profile.duration,
              temperature: profile.temperature,
              timestamp: Date.now(),
            }),
          );

          setDabLoading(false);
          navigation.navigate(Screens.Dabbing, {
            archiveProfile: profile,
          });
        }
      });
    },
    [
      dabLoading,
      peak,
      device,
      moodLights,
      verifyCanDab,
      isDeviceHeatProfile,
      updateDabbingReduxValues,
      navigation,
    ],
  );

  const renderItem = React.useCallback(
    (heatProfile: HeatProfileRenderItemType) => (
      <ProfileDisplay
        key={heatProfile.id}
        profile={heatProfile}
        moodLight={getHeatProfileMoodLight(moodLights)(heatProfile)}
        theme={theme}
        height={heatProfile.height}
        width={heatProfile.width}
        disabled={isLoading}
        onPress={() => dabWith(heatProfile)}
        isAnimating={heatProfile.id === currentProfile?.id}
        isOpal={isOpal}
        indicatorHeight={INDICATOR_HEIGHT}
        isVaporEnabled={isVaporEnabled}
      />
    ),
    [
      dabWith,
      isLoading,
      isOpal,
      moodLights,
      theme,
      currentProfile,
      isVaporEnabled,
    ],
  );

  return (
    <HeatProfileScreenBaseBackground
      theme={theme}
      onLayout={e => {
        setBackgroundHeight(e.nativeEvent.layout.height);
      }}>
      {backgroundHeight && currentProfile && (
        <ContentContainer style={{height: backgroundHeight}}>
          {backgroundHeight && (
            <Carousel
              height={backgroundHeight}
              data={isDeviceHeatProfile ? profiles : [currentProfile]}
              onSnapToItem={(index: number) => {
                if (!isDeviceHeatProfile || index === currentProfile.order)
                  return;

                peak?.cycles
                  .writeSelectedHeatCycle(index as HeatCycleArrayIndex)
                  .then(() => {
                    navigation.setParams({
                      profileId: profiles[index].id,
                    });
                  })
                  .catch(() => void 0);
              }}
              loop={isDeviceHeatProfile ? Constants.IS_WEB : false}
              currentParentIndex={isDeviceHeatProfile ? index : 0}
              {...{renderItem}}
            />
          )}

          <HeaderCover style={{height: HEADER_HEIGHT}} />

          <FooterContainer
            style={{
              marginTop: backgroundHeight - INDICATOR_HEIGHT,
            }}>
            {isDeviceHeatProfile ? (
              <ScrollIndicatorContainer>
                {profiles.map((_, i) => (
                  <Line
                    key={i}
                    style={{
                      backgroundColor:
                        i === index
                          ? theme.heatProfileSelectScreenTheme.primaryColor
                          : theme.heatProfileSelectScreenTheme
                              .backgroundLineColor,
                    }}
                  />
                ))}
              </ScrollIndicatorContainer>
            ) : (
              <StyledButton
                title="SAVE TO DEVICE"
                disabled={isLoading}
                theme={theme.styledButtonTheme}
                onPress={() =>
                  navigation.navigate(Screens.SaveToDevice, {
                    heatProfileId: currentProfile.id,
                  })
                }
              />
            )}
          </FooterContainer>
        </ContentContainer>
      )}
    </HeatProfileScreenBaseBackground>
  );
};

const HeaderCover = styled(View)({
  position: 'absolute',
  width: '100%',
  backgroundColor: colors.invisible,
});

const ContentContainer = styled(View)({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

const FooterContainer = styled(View)({
  display: 'flex',
  height: INDICATOR_HEIGHT,
  width: WIDTH,
  alignItems: 'center',
  position: 'absolute',
  flexDirection: 'column',
  justifyContent: 'flex-start',
});

const Line = styled(View)({
  width: 25,
  height: 2,
  marginHorizontal: 3.5,
});

const ScrollIndicatorContainer = styled(View)({
  height: 45,
  flexDirection: 'row',
  alignItems: 'center',
});
